import { imageMediaType } from "../native/image-formats";
/** Inspect clipboard markup in an inert template; never attach its content to the page. */
export function clipboardSource(data: DataTransfer | null) {
  const html = data?.getData("text/html") || "";
  if (html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    const source =
      template.content.querySelector("img[src]")?.getAttribute("src") || "";
    if (allowedClipboardSource(source)) return source;
  }
  const uri =
    (data?.getData("text/uri-list") || "")
      .trim()
      .split(/\r?\n/)
      .find((line) => line && !line.startsWith("#")) || "";
  return allowedClipboardSource(uri) ? uri : "";
}
export function allowedClipboardSource(source: string) {
  return /^(data:image\/|blob:|https?:\/\/)/i.test(source);
}
export function clipboardSourceName(source: string) {
  try {
    const url = new URL(source);
    if (url.protocol !== "http:" && url.protocol !== "https:")
      return "Вставленное изображение.png";
    const name = decodeURIComponent(url.pathname.split("/").pop() || "");
    if (/\.[a-z0-9]{2,5}$/i.test(name)) return name;
  } catch {
    /* Invalid or opaque names use the original fallback. */
  }
  return "Вставленное изображение.png";
}
/** Download only the explicit image source, without editor credentials or page referrer. */
export async function clipboardSourceFile(source: string): Promise<File> {
  if (!allowedClipboardSource(source))
    throw new Error("Неподдерживаемый адрес изображения.");
  try {
    const response = await fetch(source, {
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
      signal: AbortSignal.timeout(20000),
    });
    if (!response.ok)
      throw new Error("Не удалось загрузить изображение из буфера.");
    const type = (response.headers.get("content-type") || "")
      .split(";")[0]!
      .trim()
      .toLowerCase();
    if (!imageMediaType(type))
      throw new Error(
        "По этому адресу нет поддерживаемого изображения PNG, JPEG, WebP, SVG или GIF.",
      );
    const limit = 24 * 1024 * 1024;
    if (Number(response.headers.get("content-length")) > limit) {
      await response.body?.cancel();
      throw new Error("Выберите изображение до 24 МБ.");
    }
    if (!response.body) throw new Error("Пустое изображение в буфере.");
    const reader = response.body.getReader(),
      chunks: BlobPart[] = [];
    let size = 0;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > limit) {
          await reader.cancel();
          throw new Error("Выберите изображение до 24 МБ.");
        }
        chunks.push(new Uint8Array(value));
      }
    } finally {
      reader.releaseLock();
    }
    return new File(chunks, clipboardSourceName(source), { type });
  } catch (error) {
    if (
      error instanceof TypeError ||
      (error instanceof DOMException &&
        ["TimeoutError", "AbortError"].includes(error.name))
    )
      throw new Error(
        "Не удалось загрузить изображение. Сайт может запрещать доступ; скопируйте саму картинку или вставьте скриншот.",
        { cause: error },
      );
    throw error;
  }
}
